import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useToast } from "@/components/ui/use-toast";
import { BrowserBlockingPage } from "@/components/ui/browser-blocking-page";
import { BrowserCheckFallback } from "@/components/ui/browser-check-fallback";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useOrganization } from "../../App";
import { isChrome } from "@/lib/utils";

// Import our new components and hooks
import { UnifiedVideoCallSetup } from "@/components/video-call/UnifiedVideoCallSetup";
import { VideoCallInterface } from "@/components/video-call/VideoCallInterface";
import { ViolationLimitWarning } from "@/components/video-call/overlays/ViolationLimitWarning";
import { InterviewEndedDialog } from "@/components/video-call/dialogs/InterviewEndedDialog";
import { AssistantSelectionDialog } from "@/components/video-call/dialogs/AssistantSelectionDialog";
import { MonitoringConfigDialog } from "@/components/video-call/dialogs/MonitoringConfigDialog";
import { MonitoringViolationDialog } from "@/components/video-call/dialogs/MonitoringViolationDialog";


import { useCallTimer } from "@/hooks/video-call/useCallTimer";
import { useMediaControls } from "@/hooks/video-call/useMediaControls";
import { useMonitoring } from "@/hooks/video-call/useMonitoring";
import { useMonitoringWithTelemetryWrapper } from "@/hooks/video-call/useMonitoringWithTelemetryWrapper";
import { useVideoCallTelemetry } from "@/hooks/video-call/useVideoCallTelemetry";
import { useSiriWave } from "@/hooks/video-call/useSiriWave";
import { useVideoCall } from "@/hooks/video-call/useVideoCall";

import { VideoCallDataService } from "@/services/video-call/dataService";

import { DEFAULT_MONITORING_CONFIG, VIDEO_CALL_END_REASONS, type VideoCallEndReason } from "@/constants/video-call";
import { ensureNetworkEndedReason } from "@/utils/network-detection";
import {
  Assistant,
  CallConfig,
  Lead,
  MonitoringConfig,
  UIState,
  ViolationType,
  Violation,
} from "@/types/video-call";
import { RemoteParticipant, RoomEvent } from "livekit-client";
import { AudioPlayer } from "@/utils/audio";
import * as Sentry from "@sentry/react";

interface VideoCallProps {}

// Browser check wrapper component - checks mobile first, then browser
const BrowserCheckWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  try {
    // Get URL params to check if this is an interview link
    const currentPath = window.location.pathname;
    const pathParts = currentPath.split('/');
    const id = pathParts[pathParts.length - 1]; // Get the last part of the path
    const isEncodedParam = id && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
    const isInterviewLink = !!isEncodedParam;
    
    // Check for mobile devices first
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|fxios|tablet|phone|mobi|fennec/i.test(userAgent);
    const isSmallScreen = window.innerWidth <= 768;
    const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isMobile = isMobileDevice && (isSmallScreen || hasTouchScreen);
    
    // If mobile device accessing interview link, show mobile warning immediately
    if (isMobile && isInterviewLink) {
      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="mb-6">
              <svg
                className="w-16 h-16 mx-auto text-red-500 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Desktop Required
              </h1>
              <p className="text-gray-600">
                Video interviews are not supported on mobile devices
              </p>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-4 mb-6">
              <h2 className="font-semibold text-lg mb-2 text-gray-900">
                Please use a desktop or laptop with Chrome browser
              </h2>
              <ul className="text-left text-sm text-gray-700 space-y-1">
                <li>• Windows, Mac, or Linux desktop/laptop</li>
                <li>• <strong>Chrome browser only</strong> (other browsers not supported)</li>
                <li>• Working webcam and microphone</li>
                <li>• Stable internet connection</li>
              </ul>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              If you're already on a desktop, please try refreshing the page or contact support.
            </p>
          </div>
        </div>
      );
    }
    
    // If mobile but not interview link, let component handle it
    if (isMobile) {
      return <>{children}</>
    }
    
    // For desktop devices, check browser compatibility
    const chromeDetected = isChrome()
    if (!chromeDetected) {
      return <BrowserCheckFallback />
    }
    return <>{children}</>
  } catch (error) {
    Sentry.logger.error('Critical browser check error', { error: error, component: 'BrowserCheckWrapper' })
    return <BrowserCheckFallback />
  }
}

const VideoCallPageInner: React.FC<VideoCallProps> = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { currentOrganization } = useOrganization();

  // Determine if this is an interview session (base64 encoded) or regular lead ID (UUID)
  // UUIDs are exactly 36 characters, base64 strings can vary but contain +/= chars
  const isEncodedParam =
    id &&
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
  const leadId = isEncodedParam ? null : id;
  const encodedParams = isEncodedParam ? id : null;

  // Mobile detection - block mobile devices for video interviews
  const [isMobile, setIsMobile] = useState(false);
  
  // Change page title to "Hirevox" for video calls
  useEffect(() => {
    const originalTitle = document.title;
    document.title = "Hirevox";

    return () => {
      document.title = originalTitle;
    };
  }, []);

  // Initialize audio context for violation sounds
  useEffect(() => {
    AudioPlayer.ensureAudioReady().catch(error => Sentry.logger.error('AudioPlayer ensureAudioReady failed', { error: error, component: 'VideoCallPage' }))

    // Add click listener to ensure audio context is activated on user interaction
    const activateAudio = () => {
      AudioPlayer.ensureAudioReady().catch(error => Sentry.logger.error('AudioPlayer ensureAudioReady failed', { error: error, component: 'VideoCallPage' }))
      document.removeEventListener('click', activateAudio)
    }

    document.addEventListener('click', activateAudio, { once: true })

    return () => {
      document.removeEventListener('click', activateAudio)
    }
  }, []);
  
  
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|fxios|tablet|phone|mobi|fennec/i.test(userAgent);
      const isSmallScreen = window.innerWidth <= 768;
      const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isMobile = isMobileDevice && (isSmallScreen || hasTouchScreen);
      
      setIsMobile(isMobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // State for interview session
  const [isInterviewSession, setIsInterviewSession] = useState(false);
  const [interviewDetails, setInterviewDetails] = useState<any>(null);
  const [fetchingInterviewDetails, setFetchingInterviewDetails] =
    useState(false);
  const [interviewError, setInterviewError] = useState<{
    type: 'not_found' | 'expired' | 'unavailable' | 'early_access' | 'error';
    title: string;
    description: string;
  } | null>(null);
  
  // State for interview ended dialog
  const [showInterviewEndedDialog, setShowInterviewEndedDialog] = useState(false);
  const [interviewEndReason, setInterviewEndReason] = useState<string | undefined>();
  // Get URL parameters
  const searchParams = new URLSearchParams(window.location.search);
  const assistantIdFromUrl = searchParams.get("assistantId");
  const variablesFromUrl = searchParams.get("variables");

  // Parse monitoring config from URL
  const urlMonitoringConfig: Partial<MonitoringConfig> = {
    tabChangeDetection: searchParams.get("tabChangeDetection") === "true",
    externalMonitorDetection:
      searchParams.get("externalMonitorDetection") === "true",
    screenSharingEnabled: searchParams.get("screenSharing") === "true",
    blurDetection: searchParams.get("blurDetection") === "true",
    viewportDetection: searchParams.get("viewportDetection") === "true",
    echoCancellation: searchParams.get("echoCancellation") === "false" ? false : true, // Default to true if not specified
  };

  // Data state
  const [lead, setLead] = useState<Lead | null>(null);
  const [loading, setLoading] = useState(true);
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [loadingAssistants, setLoadingAssistants] = useState(false);

  // Assistant selection state
  const [selectedAssistantId, setSelectedAssistantId] = useState<string>(
    assistantIdFromUrl || ""
  );

  // Monitoring configuration
  const [monitoringConfig, setMonitoringConfig] = useState<MonitoringConfig>({
    ...DEFAULT_MONITORING_CONFIG,
    ...urlMonitoringConfig,
  });

  // UI state
  const [uiState, setUIState] = useState<UIState>({
    showPreCallPage: true,
    showAssistantDialog: false,
    dialogStep: "assistant",
    preparingCall: false,
    showSubtitles: true,
    showChatWindow: true,
    showMonitorWarning: false,
    showMonitorBlockDialog: false,
    showTabChangePenalty: false,
    showWindowBlurPenalty: false,
    violations: [],
    showViolationsPanel: true,
  });

  // Selected devices state with sessionStorage persistence
  const [selectedDevices, setSelectedDevices] = useState<{
    videoDeviceId?: string;
    audioDeviceId?: string;
    audioOutputDeviceId?: string;
  }>({});

  // Load devices from sessionStorage on page load (survives page transitions)
  useEffect(() => {
    try {
      const savedDevices = sessionStorage.getItem('voxi-selected-devices');
      if (savedDevices) {
        const parsed = JSON.parse(savedDevices);
        setSelectedDevices(parsed);
      }
    } catch (error) {
      Sentry.logger.warn('Failed to load devices from sessionStorage', error, { component: 'VideoCallPage' });
    }
  }, []);

  // Save devices to sessionStorage whenever they change
  useEffect(() => {
    if (Object.keys(selectedDevices).length > 0) {
      try {
        sessionStorage.setItem('voxi-selected-devices', JSON.stringify(selectedDevices));
      } catch (error) {
        Sentry.logger.warn('Failed to save devices to sessionStorage', error, { component: 'VideoCallPage' });
      }
    }
  }, [selectedDevices]);

  // Track device updates
  useEffect(() => {
    // Device selection updated
  }, [selectedDevices]);


  // Agent connection state
  const [agentConnected, setAgentConnected] = useState(false);
  const [agentConnecting, setAgentConnecting] = useState(false);
  const [agentFirstMessageReceived, setAgentFirstMessageReceived] = useState(false);
  
  // Agent disconnection state  
  const [agentDisconnected, setAgentDisconnected] = useState(false);
  const [agentDisconnectReason, setAgentDisconnectReason] = useState<string>("");
  const [isCallEnding, setIsCallEnding] = useState(false);
  
  // Flag to track intentional call ending (to skip beforeunload warning)
  const [isIntentionalEnding, setIsIntentionalEnding] = useState(false);


  // Variables from URL
  const [variableValues] = useState<Record<string, string>>(
    variablesFromUrl ? JSON.parse(variablesFromUrl) : {}
  );

  // Refs
  const siriWaveRef = useRef<HTMLDivElement>(null);
  const hasAutoStarted = useRef(false);

  // Create call config
  const callConfig: CallConfig = {
    assistantId: selectedAssistantId,
    leadId: leadId || "",
    organizationId: currentOrganization?.id || "",
    variables: variableValues,
    monitoring: monitoringConfig,
  };

  // Initialize hooks
  const {
    callState,
    room,
    remoteParticipants,
    conversation,
    currentSubtitle,
    isAgentSpeaking,
    networkStatus,
    connectionQuality,
    initializeVideoCall,
    endCall: originalEndCall,
  } = useVideoCall({
    leadId: leadId || interviewDetails?.lead?.id || "",
    lead,
    selectedAssistantId,
    callConfig,
    isInterviewSession,
    interviewDetails,
    encodedParams,
  });

  // Clean up sessionStorage when interview is truly complete (not just page transition)
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Only clean up if we're actually ending (not transitioning)
      if (callState.callActive === false && callState.connected === false) {
        sessionStorage.removeItem('voxi-selected-devices');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [callState.callActive, callState.connected]);

  // Persistent call ID that doesn't get cleared when call ends
  const [persistentCallId, setPersistentCallId] = useState<string | null>(null);

  // Capture and persist the call ID when it becomes available
  useEffect(() => {
    if (callState.callId && !persistentCallId) {
      setPersistentCallId(callState.callId);
    }
  }, [callState.callId, persistentCallId]);

  // Create refs for pause/resume callbacks that will be set by useMediaControls
  const pauseCallRefForMonitorDetection = useRef<
    ((reason: "monitor_detected") => void) | null
  >(null);
  const resumeCallRefForMonitorDetection = useRef<(() => void) | null>(null);

  // Create stable callbacks that use the refs
  const pauseCallForMonitorDetection = useCallback(
    (reason: "monitor_detected") => {
      pauseCallRefForMonitorDetection.current?.(reason);
    },
    []
  );

  const resumeCallForMonitorDetection = useCallback(() => {
    resumeCallRefForMonitorDetection.current?.();
  }, []);

  // Tab change penalty refs (will be set after useMediaControls)
  const pauseCallForTabChangePenaltyRef = useRef<(() => void) | null>(null);
  const resumeCallAfterTabChangePenaltyRef = useRef<(() => void) | null>(null);
  
  // Ref for setCallEndingProperly function
  const setCallEndingProperlyRef = useRef<((isEnding: boolean) => void) | null>(null);

  // Tab change penalty callbacks
  const showTabChangePenalty = useCallback(() => {
    setUIState((prev) => ({ ...prev, showTabChangePenalty: true }));
    pauseCallForTabChangePenaltyRef.current?.();
  }, []);

  const hideTabChangePenalty = useCallback(() => {
    setUIState((prev) => ({ ...prev, showTabChangePenalty: false }));
    resumeCallAfterTabChangePenaltyRef.current?.();
  }, []);

  // Window blur penalty callbacks
  const showWindowBlurPenalty = useCallback(() => {
    setUIState((prev) => ({ ...prev, showWindowBlurPenalty: true }));
    pauseCallForTabChangePenaltyRef.current?.();
  }, []);

  const hideWindowBlurPenalty = useCallback(() => {
    setUIState((prev) => ({ ...prev, showWindowBlurPenalty: false }));
    resumeCallAfterTabChangePenaltyRef.current?.();
  }, []);

  // Violation tracking
  const addViolation = useCallback((
    type: ViolationType,
    message: string,
    severity: Violation["severity"] = "warning",
    duration?: number
  ) => {
    const violation: Violation = {
      id: `${type}-${Date.now()}`,
      type,
      timestamp: new Date(),
      message,
      severity,
      duration,
    };
    
    setUIState((prev) => ({
      ...prev,
      violations: [...prev.violations, violation],
    }));

    // Play beep sound based on severity
    switch (severity) {
      case "warning":
        AudioPlayer.playWarningSound();
        break;
      case "penalty":
        AudioPlayer.playViolationSound();
        break;
      case "critical":
        AudioPlayer.playCriticalSound();
        break;
    }
  }, []);

  const clearViolations = useCallback(() => {
    setUIState((prev) => ({
      ...prev,
      violations: [],
    }));
  }, []);

  // Use callState.callId from useVideoCall hook for the actual LiveKit call ID
  // Create a reactive monitoring call ID that updates when callState.callId changes
  const monitoringCallId = useMemo(() => {
    return callState.callId || 
      (isInterviewSession ? interviewDetails?.id : leadId) || 
      id || 
      `session-${Date.now()}`;  // Final fallback to ensure we always have an ID
  }, [callState.callId, isInterviewSession, interviewDetails?.id, leadId, id]);
  const monitoringOrgId = isInterviewSession 
    ? (interviewDetails?.lead?.organization_id || interviewDetails?.agent?.organization_id)
    : currentOrganization?.id;
  
  // Debug logging for monitoring parameters
  // console.log('🔍 [VideoCallPage] useMonitoring parameters:', {
  //   isInterviewSession,
  //   monitoringCallId,
  //   monitoringOrgId,
  //   callStateCallId: callState.callId,
  //   leadId,
  //   currentOrganizationId: currentOrganization?.id,
  //   interviewDetailsId: interviewDetails?.id,
  //   interviewLeadOrgId: interviewDetails?.lead?.organization_id,
  //   interviewAgentOrgId: interviewDetails?.agent?.organization_id,
  //   hasInterviewDetails: !!interviewDetails,
  //   hasCurrentOrg: !!currentOrganization,
  //   hasCallStateCallId: !!callState.callId
  // });

  // Initialize PRIMARY telemetry hook for main session span
  const videoCallTelemetry = useVideoCallTelemetry();

  // Initialize telemetry wrapper for monitoring events (creates child spans)
  const telemetryWrapper = useMonitoringWithTelemetryWrapper({
    callId: monitoringCallId,
    organizationId: monitoringOrgId,
    callType: isInterviewSession ? "video_interview" : "voice_call",
    enableViolationTracking: true,
    enableUserInteractionTracking: true,
    enablePerformanceTracking: true,
  });

  // Wrap monitoring callbacks with telemetry
  const telemetryEnhancedAddViolation = useMemo(() => 
    telemetryWrapper.wrapAddViolation(addViolation), 
    [telemetryWrapper, addViolation]
  );

  const telemetryEnhancedTabChangePenalty = useMemo(() => 
    telemetryWrapper.wrapTabChangePenalty(showTabChangePenalty), 
    [telemetryWrapper, showTabChangePenalty]
  );

  const telemetryEnhancedWindowBlurPenalty = useMemo(() => 
    telemetryWrapper.wrapWindowBlurPenalty(showWindowBlurPenalty), 
    [telemetryWrapper, showWindowBlurPenalty]
  );

  const telemetryEnhancedPauseCall = useMemo(() => 
    telemetryWrapper.wrapPauseCall(pauseCallForMonitorDetection), 
    [telemetryWrapper, pauseCallForMonitorDetection]
  );

  const telemetryEnhancedResumeCall = useMemo(() => 
    telemetryWrapper.wrapResumeCall(resumeCallForMonitorDetection), 
    [telemetryWrapper, resumeCallForMonitorDetection]
  );

  const telemetryEnhancedEndCall = useMemo(() => 
    telemetryWrapper.wrapEndCall(originalEndCall), 
    [telemetryWrapper, originalEndCall]
  );

  // Simplified telemetry session management
  const sessionStartedRef = useRef(false);
  const lastCallIdRef = useRef<string | null>(null);
  
  useEffect(() => {
    // Only start session when we have a stable call connection
    const shouldStartSession = callState.callActive && callState.connected && callState.callId;
    const currentCallId = callState.callId; // Use the actual LiveKit call ID for consistency
    
    // Reset session tracking if call ID changes (new call)
    if (lastCallIdRef.current && lastCallIdRef.current !== currentCallId) {
      sessionStartedRef.current = false;
    }
    lastCallIdRef.current = currentCallId;
    
    // Start session once when conditions are met
    if (shouldStartSession && !sessionStartedRef.current && !videoCallTelemetry.isSessionActive()) {
      
      sessionStartedRef.current = true;
      
      videoCallTelemetry.startVideoCallSession({
        callId: currentCallId,
        organizationId: monitoringOrgId,
        leadId: interviewDetails?.lead?.id || leadId,
        assistantId: interviewDetails?.agent?.id,
        isInterviewSession,
        roomName: currentCallId, // Use callId as roomName since they're the same in our system
      });
      
      // Also start monitoring telemetry after video call session is established
      setTimeout(() => {
        telemetryWrapper.startMonitoringTelemetry();
      }, 100); // Brief delay to ensure video call session is properly initialized
    }
  }, [callState.callActive, callState.connected, callState.callId]);

  // End primary session when call ends
  useEffect(() => {
    return () => {
      console.log('🧹 Component unmounting, cleaning up video call session');
      if (videoCallTelemetry.isSessionActive()) {
        videoCallTelemetry.endVideoCallSession('component_unmount');
      }
      // Reset tracking refs
      sessionStartedRef.current = false;
      lastCallIdRef.current = null;
    };
  }, []); // Empty deps - only run on unmount

  // Heartbeat: keep last_heartbeat_at fresh so pg_cron can detect network loss
  useEffect(() => {
    const callId = callState.callId;
    if (!callState.callActive || !callState.connected || !callId) return;

    const CALL_SERVICE_URL = import.meta.env.VITE_CALL_SERVICE_URL || "http://localhost:8000";

    const sendHeartbeat = () => {
      fetch(`${CALL_SERVICE_URL}/api/call/${callId}/heartbeat`, { method: "POST" })
        .catch(() => {}); // fire-and-forget; network loss is expected here
    };

    sendHeartbeat(); // immediate ping when call becomes active
    const interval = setInterval(sendHeartbeat, 10_000);
    return () => clearInterval(interval);
  }, [callState.callActive, callState.connected, callState.callId]);

  const {
    monitoringState,
    showMonitorWarning,
    showMonitorBlockDialog,
    showExternalMonitorWarning,
    requestPermissions,
    clearMonitorWarning,
    clearMonitorBlock,
    clearExternalMonitorWarning,
    retryMonitorDetection,
    resetMonitoringStateForNewCall,
    validatedScreenStream,
    violationCounts,
    showViolationLimitWarning,
  } = useMonitoring({
    config: monitoringConfig,
    room,
    callActive: callState.callActive,
    strictMonitorMode: monitoringConfig.strictMonitorMode,
    callId: monitoringCallId,
    organizationId: monitoringOrgId,
    callType: isInterviewSession ? "video_interview" : "voice_call",
    onPauseCall: telemetryEnhancedPauseCall,
    onResumeCall: telemetryEnhancedResumeCall,
    onShowTabChangePenalty: telemetryEnhancedTabChangePenalty,
    onShowWindowBlurPenalty: telemetryEnhancedWindowBlurPenalty,
    onAddViolation: telemetryEnhancedAddViolation,
    onEndCall: telemetryEnhancedEndCall,
    isIntentionalEnding,
  });

  // Get call timer for duration tracking
  const { duration: elapsedSeconds, formattedDuration } = useCallTimer({
    isActive: callState.callActive && callState.connected,
  });

  // Use ref to store elapsedSeconds to avoid dependency re-renders
  const elapsedSecondsRef = useRef(elapsedSeconds);

  // Update ref when elapsedSeconds changes without causing callback recreations
  useEffect(() => {
    elapsedSecondsRef.current = elapsedSeconds;
  }, [elapsedSeconds]);

  // Helper function to navigate to interview complete page with real duration data
  const navigateToInterviewCompleteWithDuration = useCallback((endReason?: VideoCallEndReason) => {
    console.log('🔍 [2025-09-25T10:30:15.000Z] navigateToInterviewCompleteWithDuration called for', persistentCallId || callState.callId, '(attempt 1)');

    // Determine the final end reason - prioritize network issues if network is disconnected
    let finalEndReason = endReason;
    if (!finalEndReason) {
      if (networkStatus === 'disconnected') {
        finalEndReason = VIDEO_CALL_END_REASONS.NETWORK_CONNECTION_LOST;
        console.log('🔍 Network disconnected detected - using NETWORK_CONNECTION_LOST as end reason');
      } else {
        finalEndReason = VIDEO_CALL_END_REASONS.INTERVIEW_COMPLETED;
      }
    } else {
      // Ensure network issues override other reasons when network is disconnected
      finalEndReason = ensureNetworkEndedReason(finalEndReason, networkStatus);
    }

    console.log('🔍 navigateToInterviewCompleteWithDuration called:', {
      isInterviewSession,
      encodedParams,
      lead,
      interviewDetails,
      callState,
      networkStatus,
      providedEndReason: endReason,
      finalEndReason
    });

    // Use encodedParams to determine if this is an interview session
    // This is more reliable than isInterviewSession state which might not be set yet
    if (encodedParams || isInterviewSession) {
      // Prepare the data for the interview complete page
      // Use same name resolution logic as UnifiedVideoCallSetup
      const leadContactName = interviewDetails?.metadata?.variables?.lead_contact_name;
      const firstName = lead?.first_name;
      const emailUser = lead?.email?.split('@')[0];

      const userName = leadContactName || firstName || emailUser || 'Guest';
      // Calculate duration at call time to avoid dependency on changing formattedDuration
      const formatCallDuration = (seconds: number): string => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        if (hours > 0) {
          return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        }
        return `${minutes}:${secs.toString().padStart(2, '0')}`;
      };
      const totalTime = elapsedSecondsRef.current > 0 ? formatCallDuration(elapsedSecondsRef.current) : "0:00";
      const violationsCount = uiState.violations.length;

      // Store data in session storage as primary method
      const interviewData = {
        userName,
        totalTime,
        violationsCount,
        endReason: finalEndReason,
      };

      try {
        sessionStorage.setItem('interviewComplete', JSON.stringify(interviewData));
      } catch (error) {
        console.error('Error storing interview data:', error);
      }

      // Also pass data as URL parameters as fallback
      const searchParams = new URLSearchParams({
        userName,
        duration: totalTime,
        violations: violationsCount.toString(),
        endReason: finalEndReason,
        callId: persistentCallId || callState.callId || '', // Use persistent API call ID
        organizationId: monitoringOrgId || '',
      });

      console.log('🔍 Navigating to interview-complete with params:', searchParams.toString());
      window.location.replace(`/interview-complete?${searchParams.toString()}`);
    } else {
      // For regular calls, navigate back to dashboard
      console.log('🔍 Not an interview session, navigating to dashboard');
      window.location.replace('/dashboard');
    }
  }, [isInterviewSession, encodedParams, lead, interviewDetails, uiState.violations.length, callState.callId, persistentCallId, monitoringCallId, monitoringOrgId, networkStatus]);

  // Wrap endCall to immediately navigate to thank you page and end telemetry session
  const endCall = useCallback(async (reason?: VideoCallEndReason) => {
    try {
      setIsCallEnding(true);
      // Use utility to ensure network issues always result in NETWORK_CONNECTION_LOST
      const finalReason = ensureNetworkEndedReason(reason, networkStatus);
      if (finalReason !== reason) {
        console.log("🔍 Network disconnected - overriding reason from", reason, "to", finalReason);
      }
      
      // Set flag to indicate this is an intentional ending (skip beforeunload)
      setIsIntentionalEnding(true);
      
      // Set flag to indicate call is ending properly
      setCallEndingProperlyRef.current?.(true);
      
      // End primary telemetry session with metrics
      if (videoCallTelemetry.isSessionActive()) {
        videoCallTelemetry.endVideoCallSession(
          finalReason || 'call_ended',
          undefined, // No error
          {
            duration: elapsedSeconds,
            violationCount: Object.values(violationCounts).reduce((sum, count) => sum + count, 0),
            networkDisconnections: networkStatus === 'disconnected' ? 1 : 0,
          }
        );
      }
      
      // Call the original endCall to clean up resources
      await originalEndCall(finalReason);
      
      // Immediately navigate to thank you page
      navigateToInterviewCompleteWithDuration(finalReason);
    } catch (error) {
      console.error('Error ending call:', error);
      // Reset the flag if there's an error
      setIsIntentionalEnding(false);
      
      // End telemetry session with error
      if (videoCallTelemetry.isSessionActive()) {
        videoCallTelemetry.endVideoCallSession(
          reason || 'error_occurred',
          error instanceof Error ? error : new Error('Unknown error during call end'),
          {
            duration: elapsedSeconds,
            violationCount: Object.values(violationCounts).reduce((sum, count) => sum + count, 0),
            networkDisconnections: 0,
          }
        );
      }
      
      // Even if there's an error, still navigate away
      navigateToInterviewCompleteWithDuration();
    }
  }, [originalEndCall, navigateToInterviewCompleteWithDuration, videoCallTelemetry, elapsedSeconds, violationCounts, networkStatus]);

  // Handle interview ended dialog close
  const handleInterviewEndedDialogClose = useCallback(() => {
    // Set flag to skip beforeunload warning when navigating after dialog
    setIsIntentionalEnding(true);
    setShowInterviewEndedDialog(false);
    
    // Check if the reason is network connection lost OR if it's assistant-ended with network disconnected
    const isNetworkLost = interviewEndReason === VIDEO_CALL_END_REASONS.NETWORK_CONNECTION_LOST ||
      (networkStatus === 'disconnected' && (interviewEndReason === VIDEO_CALL_END_REASONS.ASSISTANT_ENDED || interviewEndReason === 'network-connection-lost'));
    
    if (isNetworkLost) {
      // Use history.back() to go to the previous page
      window.history.back();
    } else {
      // Navigate to completion page after dialog is closed
      navigateToInterviewCompleteWithDuration();
    }
  }, [navigateToInterviewCompleteWithDuration, interviewEndReason, networkStatus]);

  // Handle violation warning completion - show interview ended dialog
  const handleViolationWarningComplete = useCallback(async () => {
    try {
      // Check network status first - if disconnected, use NETWORK_CONNECTION_LOST
      let reason;
      if (networkStatus === 'disconnected') {
        reason = VIDEO_CALL_END_REASONS.NETWORK_CONNECTION_LOST;
        console.log("Network disconnected during violation - using NETWORK_CONNECTION_LOST");
      } else {
        // Determine the violation reason
        reason = violationCounts.tabChange >= 3 
          ? VIDEO_CALL_END_REASONS.TAB_CHANGE_VIOLATION
          : violationCounts.windowBlur >= 3 
          ? VIDEO_CALL_END_REASONS.WINDOW_BLUR_VIOLATION
          : VIDEO_CALL_END_REASONS.UNKNOWN;
      }

      // Set flag to skip beforeunload warning when ending due to violations
      setIsIntentionalEnding(true);
      setIsCallEnding(true);
      
      // Set flag to indicate call is ending properly
      setCallEndingProperlyRef.current?.(true);
      
      // Clean up the call resources
      await originalEndCall(reason);
      
      // Show the interview ended dialog
      setInterviewEndReason(reason);
      setShowInterviewEndedDialog(true);
    } catch (error) {
      console.error('Error ending call:', error);
      // Even if there's an error, still show dialog
      const reason = networkStatus === 'disconnected'
        ? VIDEO_CALL_END_REASONS.NETWORK_CONNECTION_LOST
        : violationCounts.tabChange >= 3 
        ? VIDEO_CALL_END_REASONS.TAB_CHANGE_VIOLATION
        : violationCounts.windowBlur >= 3 
        ? VIDEO_CALL_END_REASONS.WINDOW_BLUR_VIOLATION
        : VIDEO_CALL_END_REASONS.UNKNOWN;
      
      setInterviewEndReason(reason);
      setShowInterviewEndedDialog(true);
    }
  }, [violationCounts.tabChange, violationCounts.windowBlur, originalEndCall, networkStatus]);

  // Monitor for network connection lost error to show ended dialog
  useEffect(() => {
    if (callState.error === "Network connection lost" && !callState.callActive && !showInterviewEndedDialog) {
      setInterviewEndReason(VIDEO_CALL_END_REASONS.NETWORK_CONNECTION_LOST);
      setShowInterviewEndedDialog(true);
    }
    // Also handle when backend sends assistant-ended-call during network disconnection
    else if (!callState.callActive && networkStatus === 'disconnected' && !showInterviewEndedDialog && interviewEndReason === 'network-connection-lost') {
      setShowInterviewEndedDialog(true);
    }
  }, [callState.error, callState.callActive, showInterviewEndedDialog, networkStatus, interviewEndReason]);

  const {
    mediaState,
    localVideoRef,
    remoteVideoRef,
    remoteAudioRef,
    screenShareRef,
    secondaryCameraRef,
    enableLocalMedia,
    toggleMicrophone,
    toggleVideo,
    toggleSpeaker,
    startScreenShare,
    stopScreenShare,
    pauseCallForMonitorDetection: pauseCallFromMediaControls,
    resumeCall,
    pauseCallForTabChangePenalty,
    resumeCallAfterTabChangePenalty,
    dismissScreenShareEndedWarning,
    setCallEndingProperly,
    handleRemoteParticipant,
    pauseCallForBlurViolation,
    resumeCallAfterBlurViolation,
    pauseCallForCameraDisconnect,
    resumeCallAfterCameraReconnect,
    pauseCallForFaceOutOfView,
    resumeCallAfterFaceInView,
    dismissCameraDisconnectedWarning,
    handleReconnectCamera,
  } = useMediaControls({
    room,
    connected: callState.connected,
    callActive: callState.callActive,
    screenSharingEnabled: monitoringConfig.screenSharingEnabled,
    echoCancellation: monitoringConfig.echoCancellation,
    validatedScreenStream,
    callId: callState.callId,
    organizationId: currentOrganization?.id,
    selectedDevices: selectedDevices, // Pass selected devices here
    onAudioLevelChange: (level) => setWaveAmplitude(level),
    onAgentSpeaking: (speaking) => {
      if (speaking) {
        startSiriWave();
      } else {
        stopSiriWave();
      }
    },
    onEndCall: originalEndCall,
  });

  // Set the refs to the actual functions from useMediaControls
  pauseCallRefForMonitorDetection.current = pauseCallFromMediaControls;
  resumeCallRefForMonitorDetection.current = resumeCall;
  pauseCallForTabChangePenaltyRef.current = pauseCallForTabChangePenalty;
  resumeCallAfterTabChangePenaltyRef.current = resumeCallAfterTabChangePenalty;
  setCallEndingProperlyRef.current = setCallEndingProperly;

  // Face detection violation callbacks
  const handleFaceBlurDetected = useCallback(() => {
    addViolation("face_blur", "Face is blurred or out of focus", "warning");
    pauseCallForBlurViolation();
  }, [addViolation, pauseCallForBlurViolation]);

  const handleFaceBlurResolved = useCallback(() => {
    resumeCallAfterBlurViolation();
  }, [resumeCallAfterBlurViolation]);

  const handleFaceOutOfView = useCallback(() => {
    addViolation("face_out_of_view", "Face is not visible in camera", "warning");
    pauseCallForFaceOutOfView();
  }, [addViolation, pauseCallForFaceOutOfView]);

  const handleFaceInView = useCallback(() => {
    resumeCallAfterFaceInView();
  }, [resumeCallAfterFaceInView]);

  const handleCameraDisconnected = useCallback(() => {
    addViolation("camera_disconnect", "Camera disconnected", "critical");
    pauseCallForCameraDisconnect();
  }, [addViolation, pauseCallForCameraDisconnect]);

  const handleCameraReconnected = useCallback(() => {
    resumeCallAfterCameraReconnect();
  }, [resumeCallAfterCameraReconnect]);

  // Violations are now enforced consistently from the start - no need to clear

  // Listen for agent-initiated call endings
  useEffect(() => {
    if (!room) {
      console.log("🔴 No room available for data event listeners");
      return;
    }

    console.log("🟢 Setting up data event listeners on room:", room);

    const handleDataReceived = (payload: Uint8Array, participant?: any, kind?: any) => {
      try {
        const data = JSON.parse(new TextDecoder().decode(payload));
        console.log("🔵 FRONTEND: Received data message:", data, "from participant:", participant?.identity);
        
        // Check if this is the first message from agent (agent is ready)
        if (!agentFirstMessageReceived && data.type !== "call_ending" && data.type !== "error") {
          console.log("🎤 FRONTEND: Agent sent first message - ready to communicate!");
          setAgentFirstMessageReceived(true);
          setAgentConnecting(false);
        }
        
        // Check if agent is ending the call - expanded detection
        if (data.type === "call_ending" || 
            data.type === "interview_complete" || 
            data.type === "agent_ending_call" ||
            data.type === "assistant_ending" ||
            data.type === "session_complete" ||
            data.message?.includes("ending") ||
            data.message?.includes("complete") ||
            data.message?.includes("goodbye") ||
            data.message?.includes("thank you")) {
          console.log("✅ FRONTEND: Agent is ending the call:", data);
          
          // Extract reason from data if available, but check network status first
          const reason = networkStatus === 'disconnected' 
            ? VIDEO_CALL_END_REASONS.NETWORK_CONNECTION_LOST
            : (data.reason || VIDEO_CALL_END_REASONS.ASSISTANT_ENDED);
          
          // Set agent disconnected state
          setAgentDisconnected(true);
          setAgentDisconnectReason(data.message || "Interview completed by assistant");
          
          // Call endCall which will properly end the call and navigate
          console.log("🔍 Assistant ending call - calling endCall to properly terminate and navigate");
          endCall(reason);
        }
      } catch (error) {
        // Ignore non-JSON data - but log it for debugging
        console.log("🔵 FRONTEND: Non-JSON data received:", new TextDecoder().decode(payload));
      }
    };

    const handleDisconnect = (reason?: any) => {
      console.log("🔴 Room disconnected with reason:", reason);
      
      // Check if this is a proper disconnect (agent finished or server shutdown)
      if (reason === "SERVER_SHUTDOWN" || reason === "ROOM_DELETED" || reason === "PARTICIPANT_REMOVED") {
        console.log("Setting call ending properly due to disconnect reason:", reason);
        setCallEndingProperlyRef.current?.(true);
        setIsCallEnding(true);
        
        // Set flag to skip beforeunload warning for proper disconnects
        setIsIntentionalEnding(true);
        
        // Set it immediately for race condition protection
        const now = Date.now();
        console.log(`Room disconnect at ${new Date(now).toISOString()}: ${reason}`);
        
        // Navigate immediately for proper disconnects
        setTimeout(() => {
          navigateToInterviewCompleteWithDuration();
        }, 1000); // Brief delay to show disconnection
        
        // Reset after a delay
        setTimeout(() => {
          setCallEndingProperlyRef.current?.(false);
        }, 10000);
      }
    };

    room.on(RoomEvent.DataReceived, handleDataReceived);
    room.on(RoomEvent.Disconnected, handleDisconnect);

    return () => {
      room.off(RoomEvent.DataReceived, handleDataReceived);
      room.off(RoomEvent.Disconnected, handleDisconnect);
    };
  }, [room, navigateToInterviewCompleteWithDuration]);

  // Track when agent first starts speaking to hide connecting overlay
  useEffect(() => {
    if (isAgentSpeaking && !agentFirstMessageReceived) {
      console.log("🎤 FRONTEND: Agent started speaking - conversation ready!");
      setAgentFirstMessageReceived(true);
      setAgentConnecting(false);
    }
  }, [isAgentSpeaking, agentFirstMessageReceived]);

  // Note: agentConnecting timeout removed since overlay now uses connection status logic

  const { siriWave, startSiriWave, stopSiriWave, setWaveAmplitude } =
    useSiriWave({
      isAgentSpeaking,
      containerRef: siriWaveRef,
    });

  // Fetch interview details if encodedParams is present
  useEffect(() => {
    const fetchInterviewDetails = async () => {
      if (!encodedParams) return;

      setFetchingInterviewDetails(true);
      
      // Start requesting permissions early for interview sessions
      const permissionPromise = navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      }).catch(() => null); // Don't fail if permissions are denied at this stage
      
      try {
        const CALL_SERVICE_URL =
          import.meta.env.VITE_CALL_SERVICE_URL || "http://localhost:8000";
        const response = await fetch(
          `${CALL_SERVICE_URL}/api/video-call/${encodedParams}`
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);

          if (response.status === 404) {
            setInterviewError({
              type: 'not_found',
              title: "Interview Not Found",
              description: "This interview link is invalid or has expired."
            });
          } else if (
            response.status === 410 ||
            errorData?.detail === "Interview session has expired"
          ) {
            setInterviewError({
              type: 'expired',
              title: "Interview Expired",
              description: "This interview link has expired. Please request a new one."
            });
          } else if (response.status === 425) {
            setInterviewError({
              type: 'early_access',
              title: "Too early.",
              description: "This interview is scheduled for a later time. Please return at your scheduled interview time."
            });
          } else if (response.status === 409) {
            const message = errorData?.detail || "This interview session is no longer available";
            let title = "Interview Unavailable";
            let description = message;

            if (message.includes("started")) {
              title = "Interview Already Accessed";
              description = "You have already joined this interview. The link cannot be used again.";
            } else if (message.includes("completed")) {
              title = "Interview Completed";
              description = "This interview has already been completed.";
            } else if (message.includes("cancelled")) {
              title = "Interview Cancelled";
              description = "This interview session has been cancelled.";
            }

            setInterviewError({
              type: 'unavailable',
              title,
              description
            });
          } else {
            setInterviewError({
              type: 'error',
              title: "Error Loading Interview",
              description: errorData?.detail || "Failed to load interview details."
            });
          }
          return;
        }

        const data = await response.json();
        setInterviewDetails(data);
        setIsInterviewSession(true);

        // Set the lead data from interview details
        if (data.lead) {
          setLead({
            id: data.lead.id,
            first_name: data.lead.first_name || "",
            last_name: data.lead.last_name || "",
            email: data.lead.email || "",
            phone_number: data.lead.phone_number || data.lead.phone || "",
          });
        }

        // Set the assistant
        if (data.agent) {
          setSelectedAssistantId(data.agent.id);
        }

        // Set monitoring config from proctoring tools and echoCancellation
        if (data.proctoring_tools || data.echo_cancellation !== undefined) {
          setMonitoringConfig({
            ...DEFAULT_MONITORING_CONFIG,
            tabChangeDetection: data.proctoring_tools?.tab_change_detection || false,
            externalMonitorDetection:
              data.proctoring_tools?.multiple_monitor_detection || false,
            screenSharingEnabled: data.proctoring_tools?.screen_share || false,
            blurDetection: data.proctoring_tools?.face_blur_detection || false,
            viewportDetection: data.proctoring_tools?.face_out_of_view || false,
            echoCancellation: data.echo_cancellation !== undefined ? data.echo_cancellation : true,
          });
          console.log("📡 Interview session echoCancellation:", data.echo_cancellation);
        }
        
        // For interview sessions, prepare for start
        if (data && data.lead && data.agent) {
          // Pre-warm the call service connection
          const callServiceUrl = import.meta.env.VITE_CALL_SERVICE_URL || "http://localhost:8000";
          fetch(`${callServiceUrl}/health`).catch(() => {}); // Ignore errors
        }
      } catch (error) {
        console.error("Error fetching interview details:", error);
        setInterviewError({
          type: 'error',
          title: "Connection Error",
          description: "Failed to load interview details. Please check your internet connection and try again."
        });
      } finally {
        setFetchingInterviewDetails(false);
        setLoading(false);
        
        // Clean up the permission request if it's still pending
        permissionPromise.then(stream => {
          if (stream) {
            stream.getTracks().forEach(track => track.stop());
          }
        });
      }
    };

    if (encodedParams) {
      fetchInterviewDetails();
    }
  }, [encodedParams, navigate, toast]);

  // Fetch lead data
  useEffect(() => {
    const fetchLead = async () => {
      // Skip lead fetching for interview sessions or if no leadId
      if (!leadId || isInterviewSession) {
        setLoading(false);
        return;
      }

      // For regular calls, require currentOrganization
      if (!currentOrganization) {
        return;
      }

      try {
        const leadData = await VideoCallDataService.fetchLead(
          leadId,
          currentOrganization.id
        );
        setLead(leadData);
      } catch (error) {
        console.error("Error fetching lead:", error);
        setUIState((prev) => ({ ...prev, showPreCallPage: false }));
        toast({
          title: "Error",
          description: "Failed to load lead information",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchLead();
  }, [leadId, currentOrganization, toast, isInterviewSession]);

  // Fetch assistants
  const fetchAssistants = useCallback(async () => {
    // Only needed for regular calls, not interview sessions
    if (!currentOrganization || isInterviewSession) return;

    setLoadingAssistants(true);
    try {
      const assistantsData = await VideoCallDataService.fetchAssistants();
      setAssistants(assistantsData);
    } catch (error) {
      console.error("Error fetching assistants:", error);
      toast({
        title: "Error",
        description: "Failed to load assistants",
        variant: "destructive",
      });
    } finally {
      setLoadingAssistants(false);
    }
  }, [currentOrganization, toast]);

  // Get call service URL
  const getCallServiceUrl = useCallback(() => {
    return import.meta.env.VITE_CALL_SERVICE_URL || "http://localhost:8000";
  }, []);


  // Event handlers - define handleStartCall early to avoid circular dependency
  const handleStartCall = useCallback(async (skipPermissionCheck = false) => {
    // Reset ending flag when initiating a new call
    setIsCallEnding(false);
    setUIState((prev) => ({ ...prev, preparingCall: true }));

    try {
      let permissionsGranted = true;

      if (!skipPermissionCheck) {
        // Check if permissions are already granted to avoid duplicate requests
        const permissionsAlreadyGranted = 
          monitoringState.permissionStatus.camera === "granted" &&
          (!monitoringConfig.screenSharingEnabled || monitoringState.permissionStatus.screenShare === "granted") &&
          (!monitoringConfig.externalMonitorDetection || monitoringState.permissionStatus.monitorCheck === "passed");

        permissionsGranted = permissionsAlreadyGranted;
        
        if (!permissionsAlreadyGranted) {
          permissionsGranted = await requestPermissions();
        }
      } else {
        // Skipping permission check as requested by caller
      }

      if (!permissionsGranted) {
        setUIState((prev) => ({ ...prev, preparingCall: false }));
        return;
      }

      // Reset monitoring state for new call
      await resetMonitoringStateForNewCall();

      // Clear any existing violations
      clearViolations();

      
      // Reset agent connection state and start showing connecting overlay
      setAgentConnected(false);
      setAgentConnecting(true);
      setAgentFirstMessageReceived(false);
      
      setUIState((prev) => ({ ...prev, showPreCallPage: false }));
      await initializeVideoCall();
    } catch (error) {
      console.error("Error starting call:", error);
      toast({
        title: "Error",
        description: "Failed to start call. Please try again.",
        variant: "destructive",
      });
    } finally {
      setUIState((prev) => ({ ...prev, preparingCall: false }));
    }
  }, [requestPermissions, initializeVideoCall, toast, monitoringState.permissionStatus, monitoringConfig.screenSharingEnabled, monitoringConfig.externalMonitorDetection, resetMonitoringStateForNewCall, clearViolations]);


  // Auto-start call if assistant ID is provided in URL (non-interview sessions)
  useEffect(() => {
    if (
      assistantIdFromUrl &&
      !isInterviewSession &&
      lead &&
      !loading &&
      !uiState.showPreCallPage &&
      !hasAutoStarted.current
    ) {
      hasAutoStarted.current = true;
      handleStartCall();
    }
  }, [
    assistantIdFromUrl,
    isInterviewSession,
    lead,
    loading,
    uiState.showPreCallPage,
    handleStartCall,
  ]);

  // Store participant cleanup functions
  const participantCleanupsRef = useRef<Map<string, () => void>>(new Map());

  // Handle participants and clean up properly
  useEffect(() => {
    if (!room) return;

    // Function to handle a participant
    const setupParticipant = (participant: RemoteParticipant) => {
      // Skip if already handled
      if (participantCleanupsRef.current.has(participant.sid)) {
        return;
      }

      const cleanup = handleRemoteParticipant(participant);
      if (cleanup) {
        participantCleanupsRef.current.set(participant.sid, cleanup);
      }
    };

    // Handle existing participants
    room.remoteParticipants.forEach(setupParticipant);

    // Listen for new participants
    const handleParticipantConnected = (participant: RemoteParticipant) => {
      console.log("🤖 Participant connected:", participant.identity);
      
      // Check if this is the agent connecting
      const isAgent = participant.identity?.includes("agent") || 
                     participant.identity?.includes("assistant") ||
                     participant.identity?.includes("voiceagent");
      
      if (isAgent) {
        console.log("✅ Agent participant connected!");
        setAgentConnected(true);
        
        // Keep overlay visible until agent actually starts speaking
        // (overlay will be hidden when isAgentSpeaking becomes true)
      }
      
      setupParticipant(participant);
    };

    // Listen for participant disconnections
    const handleParticipantDisconnected = (participant: RemoteParticipant) => {
      console.log("Participant disconnected:", participant.identity);
      
      // Check if this is the agent disconnecting
      if (participant.identity?.includes("agent") || participant.identity?.includes("assistant")) {
        console.log("Agent has disconnected from the call");
        setAgentConnected(false);
        setAgentConnecting(false);
        setAgentDisconnected(true);
        setAgentDisconnectReason("Agent has left the interview");
        
        // For interview sessions, end call immediately when agent disconnects
        if (encodedParams || isInterviewSession) {
          console.log("Interview session - agent disconnected, ending call and navigating to ending page");
          // Check network status to determine the correct ended reason
          const endedReason = networkStatus === 'disconnected' 
            ? VIDEO_CALL_END_REASONS.NETWORK_CONNECTION_LOST 
            : VIDEO_CALL_END_REASONS.ASSISTANT_ENDED;
          console.log("Agent disconnect - network status:", networkStatus, "ended reason:", endedReason);
          // Call endCall which will properly end the call and navigate
          endCall(endedReason);
        } else {
          // For regular calls, wait before checking to allow for reconnection
          setTimeout(() => {
            // For regular calls, check if room is disconnected
            if (room?.state === "disconnected" || !room) {
              console.log("Call ending properly flag set to: true");
              // Set flag to indicate call is ending properly
              setIsCallEnding(true);
              setCallEndingProperlyRef.current?.(true);
              
              // Set flag to skip beforeunload warning when agent disconnects
              setIsIntentionalEnding(true);
              
              // Navigate to dashboard for regular calls
              navigateToInterviewCompleteWithDuration();
              
              // Reset after a delay
              setTimeout(() => {
                setCallEndingProperlyRef.current?.(false);
              }, 10000);
            } else {
              console.log("Agent reconnected - not ending call");
              // Reset disconnection state if room is still connected
              setAgentDisconnected(false);
              setAgentDisconnectReason("");
            }
          }, 3000); // Wait 3 seconds before considering it a proper disconnect
        }
      }
      
      const cleanup = participantCleanupsRef.current.get(participant.sid);
      if (cleanup) {
        cleanup();
        participantCleanupsRef.current.delete(participant.sid);
      }
    };

    room.on(RoomEvent.ParticipantConnected, handleParticipantConnected);
    room.on(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);

    // Cleanup
    return () => {
      room.off(RoomEvent.ParticipantConnected, handleParticipantConnected);
      room.off(
        RoomEvent.ParticipantDisconnected,
        handleParticipantDisconnected
      );

      // Clean up all participant handlers
      participantCleanupsRef.current.forEach((cleanup) => cleanup());
      participantCleanupsRef.current.clear();
    };
  }, [room, handleRemoteParticipant, navigateToInterviewCompleteWithDuration]);

  // Enable local media when room is connecting (not waiting for full connection)
  useEffect(() => {
    if (room && (callState.connecting || callState.connected)) {
      // Enable media immediately when connecting starts
      const mediaDelay = isInterviewSession ? 0 : 200;
      setTimeout(() => {
        console.log("Enabling local media during connecting state");
        console.log("🎯 [VideoCallPage] selectedDevices at enableLocalMedia call:", selectedDevices);
        enableLocalMedia(room);
      }, mediaDelay);

      // Auto-start screen sharing if screen sharing is enabled and room is connected
      // Check both callState.connected and room.state for more reliable connection detection
      const isRoomReady = (callState.connected || room?.state === "connected") && monitoringConfig.screenSharingEnabled;
      
      // CRITICAL: Prevent auto-start if there are any active violations
      const hasActiveViolations = mediaState.showScreenShareEndedWarning || 
                                 mediaState.showCameraDisconnectedWarning ||
                                 mediaState.screenShareInterrupted ||
                                 mediaState.cameraInterrupted ||
                                 (mediaState.callPaused && (
                                   mediaState.pauseReason === "screen_share_lost" ||
                                   mediaState.pauseReason === "camera_lost"
                                 ));
      
      if (isRoomReady && !mediaState.screenSharingActive && !hasActiveViolations) {
        // Auto-start screen share evaluation

        // Immediate start with minimal delay to ensure room is ready
        setTimeout(() => {
          // Double-check that screen sharing hasn't already started and no violations occurred
          if (!mediaState.screenSharingActive && !mediaState.showScreenShareEndedWarning) {
            startScreenShare();
          } else {
            // Auto-start skipped - Screen sharing already active or violation detected
          }
        }, 5000); // Increased to 5s for LiveKit Cloud stability - allows full negotiation completion
      } else {
        // Auto-start screen share skipped
      }
    }
  }, [
    room,
    callState.connecting,
    callState.connected,
    callState.callActive,
    monitoringConfig.screenSharingEnabled,
    validatedScreenStream,
    startScreenShare,
    isInterviewSession,
    mediaState.screenSharingActive,
    // CRITICAL: Include violation states to prevent auto-start during violations
    mediaState.showScreenShareEndedWarning,
    mediaState.showCameraDisconnectedWarning,
    mediaState.screenShareInterrupted,
    mediaState.cameraInterrupted,
    mediaState.callPaused,
    mediaState.pauseReason,
  ]);

  const handleManualStartCall = useCallback(() => {
    setUIState((prev) => ({
      ...prev,
      showAssistantDialog: true,
      dialogStep: "assistant",
    }));
    fetchAssistants();
  }, [fetchAssistants]);

  const handleAssistantNext = useCallback(() => {
    setUIState((prev) => ({ ...prev, dialogStep: "monitoring" }));
  }, []);

  const handleAssistantBack = useCallback(() => {
    setUIState((prev) => ({ ...prev, dialogStep: "assistant" }));
  }, []);

  const handleConfigChange = useCallback(
    (key: keyof MonitoringConfig, value: any) => {
      setMonitoringConfig((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const handleDialogStartCall = useCallback(async () => {
    setUIState((prev) => ({
      ...prev,
      showAssistantDialog: false,
      showPreCallPage: false,
    }));
    await initializeVideoCall();
  }, [initializeVideoCall]);

  const handleCloseDialog = useCallback(() => {
    setUIState((prev) => ({
      ...prev,
      showAssistantDialog: false,
      dialogStep: "assistant",
    }));
  }, []);

  const handleToggleSubtitles = useCallback(() => {
    setUIState((prev) => ({ ...prev, showSubtitles: !prev.showSubtitles }));
  }, []);

  const handleToggleChatWindow = useCallback(() => {
    setUIState((prev) => ({ ...prev, showChatWindow: !prev.showChatWindow }));
  }, []);

  const handleNavigateBack = useCallback(() => {
    // Close window for all cases since this page should be standalone
    window.close();
  }, []);

  const handleRetryScreenShare = useCallback(async () => {
    setUIState((prev) => ({ ...prev, preparingCall: true }));
    try {
      const permissionsGranted = await requestPermissions();
      if (!permissionsGranted) {
        toast({
          title: "Screen Share Required",
          description: "Please share your entire screen to continue",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error retrying screen share:", error);
      toast({
        title: "Error",
        description: "Failed to retry screen share. Please try again.",
        variant: "destructive",
      });
    } finally {
      setUIState((prev) => ({ ...prev, preparingCall: false }));
    }
  }, [requestPermissions, toast]);




  // Loading state
  if (loading || fetchingInterviewDetails) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner
          text={
            fetchingInterviewDetails
              ? "Loading interview details..."
              : "Loading lead information..."
          }
        />
      </div>
    );
  }

  // Interview error state
  if (interviewError) {
    const getIcon = () => {
      switch (interviewError.type) {
        case 'not_found':
        case 'expired':
          return (
            <svg className="w-16 h-16 mx-auto text-orange-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          );
        case 'early_access':
          return (
            <svg className="w-16 h-16 mx-auto text-yellow-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          );
        case 'unavailable':
          return (
            <svg className="w-16 h-16 mx-auto text-blue-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          );
        case 'error':
        default:
          return (
            <svg className="w-16 h-16 mx-auto text-red-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          );
      }
    };

    const handleRetryClick = () => {
      // Retry loading interview details
      setInterviewError(null);
      setFetchingInterviewDetails(true);
      // The useEffect will automatically retry when encodedParams is still present
    };

    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mb-6">
            {getIcon()}
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {interviewError.title}
            </h1>
            <p className="text-gray-600">
              {interviewError.description}
            </p>
          </div>
          
          {interviewError.type === 'error' ? (
            <Button 
              onClick={handleRetryClick}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white mb-4"
            >
              Try Again
            </Button>
          ) : (
            <div className="bg-blue-50 rounded-lg p-4 mb-6">
              <h2 className="font-semibold text-lg mb-2 text-gray-900">
                Need help?
              </h2>
              <ul className="text-left text-sm text-gray-700 space-y-1">
                <li>• Contact the person who sent you this interview link</li>
                <li>• Request a new interview link if expired</li>
                <li>• Ensure you're using the correct link</li>
              </ul>
            </div>
          )}
          
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">
              You can safely close this tab or browser window
            </p>
          </div>
          
          <p className="text-xs text-gray-500 mt-4">
            If you continue to experience issues, please contact support.
          </p>
        </div>
      </div>
    );
  }

  // Mobile blocking is now handled at BrowserCheckWrapper level
  // This component-level check is no longer needed
  // console.log('🔍 Component Level - Mobile blocking handled by wrapper:', {
  //   isMobile,
  //   isInterviewSession,
  //   encodedParams,
  //   leadId
  // });

  // Lead not found state
  if (!lead) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">
            Lead Not Found
          </h1>
          <Button onClick={handleNavigateBack}>Close Window</Button>
        </div>
      </div>
    );
  }



  // Show unified setup screen for regular calls or interview sessions
  if (uiState.showPreCallPage) {
    return (
      <>
        <UnifiedVideoCallSetup
          lead={lead}
          config={monitoringConfig}
          monitoringState={monitoringState}
          preparingCall={uiState.preparingCall}
          onStartCall={
            assistantIdFromUrl || isInterviewSession
              ? handleStartCall
              : handleManualStartCall
          }
          onNavigateBack={handleNavigateBack}
          onDeviceSelectionChange={setSelectedDevices}
          onRequestPermissions={requestPermissions}
          onRetryMonitorDetection={retryMonitorDetection}
          isInterviewSession={isInterviewSession}
          interviewDetails={interviewDetails}
          selectedAssistantId={selectedAssistantId}
          onAssistantSelection={() => setUIState((prev) => ({ ...prev, showAssistantDialog: true }))}
          callActive={callState.callActive}
          onAddViolation={addViolation}
        />

        {/* Assistant Selection Dialog */}
        <AssistantSelectionDialog
          open={uiState.showAssistantDialog}
          assistants={assistants}
          selectedAssistantId={selectedAssistantId}
          loadingAssistants={loadingAssistants}
          dialogStep={uiState.dialogStep}
          onAssistantChange={setSelectedAssistantId}
          onNextStep={handleAssistantNext}
          onClose={handleCloseDialog}
        />

        {/* Monitoring Config Dialog */}
        <MonitoringConfigDialog
          open={uiState.showAssistantDialog}
          config={monitoringConfig}
          dialogStep={uiState.dialogStep}
          onConfigChange={handleConfigChange}
          onStartCall={handleDialogStartCall}
          onBackStep={handleAssistantBack}
          onClose={handleCloseDialog}
        />

        {/* Monitoring Violation Dialogs */}
        <MonitoringViolationDialog
          showWarning={showMonitorWarning}
          showBlock={showMonitorBlockDialog}
          monitorResult={monitoringState.currentMonitorResult}
          action={monitoringConfig.monitorDetectionAction}
          onDismissWarning={clearMonitorWarning}
          onDismissBlock={clearMonitorBlock}
        />

        {/* 🔍 DEBUG: Log call ID values before passing to ViolationLimitWarning */}
        {(() => {
          if (showViolationLimitWarning) {
          }
          return null;
        })()}
        
        <ViolationLimitWarning
          show={showViolationLimitWarning}
          violationType={violationCounts.tabChange >= 3 ? "tab_change" : violationCounts.windowBlur >= 3 ? "window_blur" : undefined}
          onWarningComplete={handleViolationWarningComplete}
          violationCount={Math.max(violationCounts.tabChange, violationCounts.windowBlur)}
          roomDisconnected={!callState.connected && !callState.callActive}
          callId={persistentCallId || callState.callId}
          organizationId={monitoringOrgId}
        />
      </>
    );
  }

  // Show main video call interface
  return (
    <>
    
    <VideoCallInterface
      lead={lead}
      connected={callState.connected}
      connecting={callState.connecting}
      callActive={callState.callActive}
      remoteParticipants={remoteParticipants}
      mediaState={mediaState}
      currentSubtitle={currentSubtitle}
      showSubtitles={uiState.showSubtitles}
      isAgentSpeaking={isAgentSpeaking}
      conversation={conversation}
      showChatWindow={uiState.showChatWindow}
      callDuration={formattedDuration}
      networkStatus={networkStatus}
      connectionQuality={connectionQuality}
      localVideoRef={localVideoRef}
      remoteVideoRef={remoteVideoRef}
      remoteAudioRef={remoteAudioRef}
      screenShareRef={screenShareRef}
      secondaryCameraRef={secondaryCameraRef}
      siriWaveRef={siriWaveRef}
      onToggleMicrophone={toggleMicrophone}
      onToggleVideo={toggleVideo}
      onToggleSpeaker={toggleSpeaker}
      onToggleSubtitles={handleToggleSubtitles}
      onToggleChatWindow={handleToggleChatWindow}
      onStartScreenShare={startScreenShare}
      onStopScreenShare={stopScreenShare}
      onEndCall={endCall}
      screenSharingEnabled={monitoringConfig.screenSharingEnabled}
      showTabChangePenalty={uiState.showTabChangePenalty}
      onTabChangePenaltyComplete={hideTabChangePenalty}
      onDismissScreenShareWarning={dismissScreenShareEndedWarning}
      showWindowBlurPenalty={uiState.showWindowBlurPenalty}
      onWindowBlurPenaltyComplete={hideWindowBlurPenalty}
      onDismissCameraWarning={dismissCameraDisconnectedWarning}
      onReconnectCamera={handleReconnectCamera}
      violations={uiState.violations}
      isInterviewSession={isInterviewSession}
      agentConnected={agentConnected}
      agentConnecting={agentConnecting}
      agentDisconnected={agentDisconnected}
      agentDisconnectReason={agentDisconnectReason}
      isCallEnding={isCallEnding}
      showViolationsPanel={uiState.showViolationsPanel}
      showExternalMonitorWarning={showExternalMonitorWarning}
      externalMonitorScreenCount={monitoringState.currentScreenCount}
      externalMonitorPreviousScreenCount={monitoringState.previousScreenCount}
      onDismissExternalMonitorWarning={clearExternalMonitorWarning}
      callId={persistentCallId || callState.callId || monitoringCallId}
      organizationId={monitoringOrgId}
    />
    
    <ViolationLimitWarning
      show={showViolationLimitWarning}
      violationType={violationCounts.tabChange >= 3 ? "tab_change" : violationCounts.windowBlur >= 3 ? "window_blur" : undefined}
      onWarningComplete={handleViolationWarningComplete}
      violationCount={Math.max(violationCounts.tabChange, violationCounts.windowBlur)}
      roomDisconnected={!callState.connected && !callState.callActive}
      callId={persistentCallId || callState.callId}
      organizationId={monitoringOrgId}
    />
    
    <InterviewEndedDialog
      open={showInterviewEndedDialog}
      reason={interviewEndReason}
      onClose={handleInterviewEndedDialogClose}
      networkDisconnected={networkStatus === 'disconnected'}
    />

    
    </>
  );
};

const VideoCallPage: React.FC<VideoCallProps> = () => {
  return (
    <BrowserCheckWrapper>
      <VideoCallPageInner />
    </BrowserCheckWrapper>
  )
}

export default VideoCallPage;
